// Admin-Seite: Backup-Center
// Designer-Wireframe umgesetzt + Sicherheits-Gate (Passwort + typed confirm).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, History, Clock, CalendarDays, Download, Undo2,
  AlertOctagon, Lock, Zap, CheckCircle2, AlertTriangle, Loader2, RefreshCw,
} from 'lucide-react';
import {
  getStatus, listBackups, manualBackup, restoreBackup, timeTravelRestore,
  downloadBackup,
  type BackupStatus, type BackupObj,
} from '../lib/adminApi';
import { useEscClose } from '../hooks/useEscClose';

type RestoreTarget =
  | { kind: 'r2'; key: string; label: string }
  | { kind: 'tt'; timestamp: string; label: string };

export default function AdminBackups() {
  const [adminPw, setAdminPw] = useState(
    () => sessionStorage.getItem('sickinger_admin_pw') ?? ''
  );
  const [unlocked, setUnlocked] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);

  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [fiveMin, setFiveMin] = useState<BackupObj[]>([]);
  const [daily, setDaily] = useState<BackupObj[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyMsg, setBusyMsg] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const [restoreTarget, setRestoreTarget] = useState<RestoreTarget | null>(null);
  const [confirmPw, setConfirmPw] = useState('');
  const [confirmTyped, setConfirmTyped] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);

  useEscClose(() => setRestoreTarget(null));

  const [ttDateTime, setTtDateTime] = useState(
    () => new Date(Date.now() - 3600_000).toISOString().slice(0, 16)
  );

  // ---- Loader ----
  async function loadAll(pw: string) {
    setLoading(true);
    setGateError(null);
    try {
      const [s, m5, md] = await Promise.all([
        getStatus(pw),
        listBackups(pw, '5min/'),
        listBackups(pw, 'daily/'),
      ]);
      setStatus(s);
      setFiveMin(m5.objects);
      setDaily(md.objects);
      sessionStorage.setItem('sickinger_admin_pw', pw);
      setUnlocked(true);
    } catch (e) {
      setGateError((e as Error).message);
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (adminPw) loadAll(adminPw);
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    const t = setInterval(() => loadAll(adminPw), 30_000);
    return () => clearInterval(t);
  }, [unlocked, adminPw]);

  // ---- Aktionen ----
  async function doManualBackup() {
    setBusyMsg('Manueller Backup läuft …');
    try {
      await manualBackup(adminPw);
      showToast('ok', 'Manueller Backup erstellt');
      await loadAll(adminPw);
    } catch (e) {
      showToast('err', (e as Error).message);
    } finally {
      setBusyMsg(null);
    }
  }

  async function doDownload(b: BackupObj) {
    try {
      const blob = await downloadBackup(adminPw, b.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.key.split('/').pop() ?? 'backup.sql';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('err', (e as Error).message);
    }
  }

  async function performRestore() {
    if (!restoreTarget) return;
    if (confirmPw !== adminPw) {
      showToast('err', 'Passwort falsch');
      return;
    }
    if (confirmTyped !== 'RESTORE' || !confirmChecked) {
      showToast('err', 'Bestätigung unvollständig');
      return;
    }
    setBusyMsg('Restore läuft — nicht schließen!');
    try {
      if (restoreTarget.kind === 'r2') {
        await restoreBackup(adminPw, restoreTarget.key);
      } else {
        await timeTravelRestore(adminPw, {
          timestamp: new Date(restoreTarget.timestamp).toISOString(),
        });
      }
      showToast('ok', 'Restore erfolgreich — Seite wird neu geladen …');
      setRestoreTarget(null);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) {
      showToast('err', (e as Error).message);
    } finally {
      setBusyMsg(null);
    }
  }

  function showToast(kind: 'ok' | 'err', msg: string) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  }

  function openR2Restore(b: BackupObj) {
    setRestoreTarget({
      kind: 'r2',
      key: b.key,
      label: `Backup vom ${new Date(b.uploaded).toLocaleString('de-DE')} · ${(b.size / 1024 / 1024).toFixed(2)} MB`,
    });
    setConfirmPw('');
    setConfirmTyped('');
    setConfirmChecked(false);
  }

  function openTimeTravelRestore() {
    setRestoreTarget({
      kind: 'tt',
      timestamp: ttDateTime,
      label: `Time Travel zu ${new Date(ttDateTime).toLocaleString('de-DE')}`,
    });
    setConfirmPw('');
    setConfirmTyped('');
    setConfirmChecked(false);
  }

  // ---- Locked View ----
  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white rounded-lg shadow-md border p-6 w-full max-w-md">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gray-700" />
            <h1 className="text-xl font-bold">Admin Backup Center</h1>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Zweiter Passwort-Faktor erforderlich (nicht das normale Site-Passwort).
          </p>
          <input
            type="password"
            value={adminPw}
            onChange={(e) => setAdminPw(e.target.value)}
            className="w-full border-2 border-gray-300 focus:border-brand rounded-md px-3 py-2 font-mono mb-3"
            placeholder="Admin-Passwort"
            autoFocus
          />
          <button
            onClick={() => loadAll(adminPw)}
            disabled={loading || !adminPw}
            className="w-full bg-brand hover:bg-brand-dark disabled:bg-gray-300 text-white px-4 py-2 rounded-md font-medium inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Entsperren
          </button>
          {gateError && (
            <p className="text-red-600 text-sm mt-3">{gateError}</p>
          )}
          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:underline">← Zurück zum Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Main View ----
  const healthColor =
    status?.health === 'green' ? 'bg-green-100 text-green-800' :
    status?.health === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
    status?.health === 'red' ? 'bg-red-100 text-red-800 animate-pulse' :
    'bg-gray-100 text-gray-800';
  const HealthIcon =
    status?.health === 'green' ? CheckCircle2 :
    status?.health === 'red' ? AlertTriangle : Clock;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-brand hover:underline">← Dashboard</Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" /> Backup &amp; Recovery
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${healthColor}`}>
              <HealthIcon className="w-4 h-4" />
              {status.gap_minutes >= 0
                ? `Letzter Backup: vor ${status.gap_minutes} Min`
                : 'Noch keine Backups'}
            </span>
          )}
          <button
            onClick={() => loadAll(adminPw)}
            className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Neu laden
          </button>
          <button
            onClick={doManualBackup}
            disabled={!!busyMsg}
            className="inline-flex items-center gap-1 bg-brand hover:bg-brand-dark text-white px-3 py-1.5 rounded-md text-sm font-medium"
          >
            <Zap className="w-4 h-4" /> Jetzt sichern
          </button>
        </div>
      </div>

      {busyMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-md flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {busyMsg}
        </div>
      )}

      {/* Card 1: Time Travel */}
      <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <History className="w-5 h-5" /> D1 Time Travel (Point-in-Time)
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          Sekundengenau zurück bis 30 Tage. Nutzt den eingebauten Cloudflare-Mechanismus.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Zeitpunkt</label>
            <input
              type="datetime-local"
              value={ttDateTime}
              onChange={(e) => setTtDateTime(e.target.value)}
              className="border rounded-md px-3 py-2"
            />
          </div>
          <button
            onClick={openTimeTravelRestore}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center gap-2"
          >
            <Undo2 className="w-4 h-4" /> Wiederherstellen
          </button>
        </div>
      </div>

      {/* Card 2: 5-min */}
      <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5" /> 5-Min-Backups (R2) · 12h Retention
        </h2>
        <BackupTable items={fiveMin} onDownload={doDownload} onRestore={openR2Restore} />
      </div>

      {/* Card 3: Daily */}
      <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Tägliche Backups (R2) · 30 Tage
        </h2>
        <BackupTable items={daily} onDownload={doDownload} onRestore={openR2Restore} />
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-md shadow-lg text-white flex items-center gap-2 z-50 ${
            toast.kind === 'ok' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.kind === 'ok' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {toast.msg}
        </div>
      )}

      {/* Restore Modal */}
      {restoreTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg border-2 border-red-600 max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertOctagon className="w-10 h-10 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-red-800">GEFAHR — DB überschreiben</h3>
                <p className="text-sm text-gray-700 mt-1">{restoreTarget.label}</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-300 text-red-800 text-xs p-2 rounded">
              Die aktuelle Produktions-DB wird überschrieben. Vor jedem Restore wird
              automatisch ein Pre-Snapshot angelegt — im Notfall kann dieser wiederhergestellt werden.
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Admin-Passwort</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full border-2 border-gray-300 focus:border-red-600 rounded-md px-3 py-2 font-mono"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">
                Zum Bestätigen „RESTORE" tippen
              </label>
              <input
                type="text"
                value={confirmTyped}
                onChange={(e) => setConfirmTyped(e.target.value)}
                className="w-full border-2 border-gray-300 focus:border-red-600 rounded-md px-3 py-2 font-mono"
                placeholder="RESTORE"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="w-5 h-5 accent-red-600"
              />
              JA, ich überschreibe die Produktions-DB
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setRestoreTarget(null)}
                className="flex-1 px-4 py-2 border rounded-md font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={performRestore}
                disabled={
                  !confirmPw ||
                  confirmTyped !== 'RESTORE' ||
                  !confirmChecked ||
                  !!busyMsg
                }
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md font-bold inline-flex items-center justify-center gap-2"
              >
                <Undo2 className="w-4 h-4" /> JETZT WIEDERHERSTELLEN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Sub-Component: Backup Tabelle ----
function BackupTable({
  items,
  onDownload,
  onRestore,
}: {
  items: BackupObj[];
  onDownload: (b: BackupObj) => void;
  onRestore: (b: BackupObj) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6">
        Noch keine Backups vorhanden
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-600 border-b">
            <th className="py-2 pr-2">Zeit</th>
            <th className="py-2 pr-2">Größe</th>
            <th className="py-2 pr-2">Status</th>
            <th className="py-2 pr-2">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.key} className="border-b hover:bg-gray-50">
              <td className="py-2 pr-2">
                {new Date(b.uploaded).toLocaleString('de-DE', {
                  dateStyle: 'short',
                  timeStyle: 'medium',
                })}
              </td>
              <td className="py-2 pr-2">{(b.size / 1024 / 1024).toFixed(2)} MB</td>
              <td className="py-2 pr-2">
                <span className="inline-flex items-center gap-1 text-green-700">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> OK
                </span>
              </td>
              <td className="py-2 pr-2">
                <button
                  onClick={() => onDownload(b)}
                  className="text-brand hover:text-brand-dark inline-flex gap-1 items-center text-sm mr-3"
                >
                  <Download className="w-4 h-4" /> DL
                </button>
                <button
                  onClick={() => onRestore(b)}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm inline-flex items-center gap-1"
                >
                  <Undo2 className="w-3 h-3" /> Restore
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
